import { inject, injectable } from 'inversify';
import { serializeError } from 'serialize-error';
import NotFoundError from '../../../errors/NotFoundError';
import { retryAsync } from '../../../helpers/retry';
import { TOKEN } from '../../../inversify.tokens';
import type { TrackLabel } from '../../../types/common';
import type Logger from '../../Logger';
import type Network from '../../network';
import PendingClosureProducerStorage, {
  PendingClosureProducer,
} from './PendingClosureProducerStorage';
import { isExpectedSignalingError } from '../../../errors/isExpectedSignalingError';

@injectable()
class ProducerCloseManager {
  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.Network) private readonly network: Network,
    @inject(TOKEN.PendingClosureProducerStorage) private readonly storage: PendingClosureProducerStorage,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
  ) {
    this.#logger = loggerFactory('ProducerCloseManager');
  }

  async close(label: TrackLabel, producerId: string): Promise<void> {
    try {
      const closed = await this.sendCloseRequest(producerId);
      this.storage.delete(label);

      if (!closed) {
        return;
      }

      this.#logger.info('Remote producer closed', { label, producerId });
    } catch (error) {
      this.storage.set(label, producerId);
      const logAction = isExpectedSignalingError(error)
        ? this.#logger.debug.bind(this.#logger)
        : this.#logger.warn.bind(this.#logger);
      logAction('Failed to close remote producer, queued for retry', {
        label,
        producerId,
        error: serializeError(error),
      });
    }
  }

  async ensureNoPending(label: TrackLabel): Promise<void> {
    const pending = this.storage.get(label);

    if (!pending) {
      return;
    }

    await this.flushEntry(pending);
  }

  async flushPending(): Promise<void> {
    const entries = this.storage.entries();

    for (const entry of entries) {
      try {
        await this.flushEntry(entry);
      } catch {}
    }
  }

  private async flushEntry(entry: PendingClosureProducer): Promise<void> {
    try {
      await retryAsync(
        async () => {
          await this.sendCloseRequest(entry.producerId);
        },
        {
          maxRetries: 5,
          minBackoffDelayMs: 200,
          maxBackoffDelayMs: 1000,
          actionName: 'closeRemoteProducer',
          logger: this.#logger,
        },
      );

      this.storage.delete(entry.label);
      this.#logger.info('Pending remote producer closed', {
        label: entry.label,
        producerId: entry.producerId,
      });
    } catch (error) {
      this.#logger.debug('Failed to close pending remote producer', {
        label: entry.label,
        producerId: entry.producerId,
        error: serializeError(error),
      });

      throw error;
    }
  }

  private async sendCloseRequest(producerId: string): Promise<boolean> {
    try {
      await this.network.closeRemoteProducer(producerId);

      return true;
    } catch (error) {
      if (error instanceof NotFoundError) {
        this.#logger.debug('Remote producer maybe already closed', {
          producerId,
        });

        return false;
      }

      throw error;
    }
  }
}

export default ProducerCloseManager;
